import React, { FunctionComponent, ReactNode, useRef, useState } from 'react'
import { Grid, Text, Icon, Tooltip, CARET_PLACEMENTS, useIsDesignLanguage, DesignLanguage, Divider, GUTTER_VARIANT } from '@afterpaytouch/core'
import styles from './style.module.css'
import {
  useAppRouter,
  useCachedFeatureFlag,
  useCalculatedPaymentSchedules,
  useCheckoutState,
  useCheckoutToken,
  useCrossBorderTrade,
  useDonation,
  useDonationCampaignDetails,
  useInitialData,
  useIsCheckoutLoadingOrUpdating,
  useIsConsumerLendingOrder,
  useIsCountry,
  useIsDeferredShipping,
  useIsEstimatedPPA,
  useIsExpress,
  useIsSupOrder,
  useShippingOption,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { Money, PromoCodeInput, TotalAmountPayable } from '..'
import { Trans } from '../../utils/convergence'
import { useTranslation } from '../../utils/convergence'
import { AddressModeType, Features, nonShippingInfoAddressModes, money } from '../../model'
import { isNullOrUndefined, valueIsNotNullOrUndefined } from '@afterpay/utils'
import { useIsOrderBreakdownVisible, useOrderBreakdownType } from './hooks'
import { Country, Money as MoneyObject } from '@afterpay/types'
import { useMoney } from '../Money/useMoney'
import { FirstPaymentType } from '@afterpaytouch/portal-api'

const GridItem = Grid.Item
const GridFixedItem = Grid.FixedItem

// Shared by the Tooltip's `id` and the trigger's `aria-describedby` so the two cannot drift.
const DISCOUNT_TOOLTIP_ID = 'order-breakdown-discount-tooltip'

interface OrderBreakdownProps {
  name: string
  amount?: MoneyObject
  loading?: boolean
  preferFreeText?: boolean
}

const OrderBreakdownItem: FunctionComponent<OrderBreakdownProps> = ({ name, amount, loading, preferFreeText = false }): JSX.Element => {
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { locale } = useAppRouter()
  const { isZeroAmount } = useMoney({ locale })

  const testNamespace = `order-breakdown-${name}`
  const itemTextSize = isArcadeDesignLanguage ? textSize : smallTextSize
  const textColor = isArcadeDesignLanguage ? 'Standard' : 'Subtle'
  const amountShowing =
    isZeroAmount(amount?.amount) && preferFreeText ? (
      <Text size={itemTextSize} testNameSpace={`${testNamespace}-free`} color={textColor}>
        {t('summary:free')}
      </Text>
    ) : (
      <Money loading={loading} color={textColor} locale={locale} size={itemTextSize} value={amount} testNameSpace={testNamespace} />
    )

  return (
    <Grid justifyContent='space-between'>
      <GridItem>
        <Text size={itemTextSize} color={textColor}>
          {name}
        </Text>
      </GridItem>
      <GridFixedItem align='right'>{amountShowing}</GridFixedItem>
    </Grid>
  )
}

// Pure helper — true iff the active promotion is the one the user applied via
// PromoCodeInput in this checkout (token-scoped marker matches the promo code).
// Lives at module level so it doesn't capture component state.
const isPromoCodeUserApplied = (
  promotion: { code: string } | null | undefined,
  marker: { token: string; code: string } | null | undefined,
  token: string
): boolean => {
  if (!promotion || !marker) return false
  if (marker.token !== token) return false
  return marker.code === promotion.code
}

/**
 * This component is used to planned GA design
 */
export const OrderBreakdown = (): JSX.Element => {
  const { started, checkout, firstPaymentType, manualPromoCode: persistedManualPromo } = useCheckoutState()
  const { locale } = useAppRouter()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { smallTextSize, textSize, extraSmallTextSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isExpressCheckout = useIsExpress()
  const { addressMode } = useInitialData()
  const { isCrossBorder } = useCrossBorderTrade()
  const { selectedShippingOption: nonXbtSelectedShippingOption, xbtAllShippingOptions } = useShippingOption()
  const isShippingOptionUpdating = useIsCheckoutLoadingOrUpdating()
  const [showDiscountTooltip, setShowDiscountTooltip] = useState(false)
  const discountTooltipRef = useRef<HTMLButtonElement>(null)
  const { formatMoney } = useMoney({
    locale,
    currency: started?.promotion?.discount.currency,
    decimalPlaces: 2,
  })
  const { donationAmount, isUpdatingDonation } = useDonation()
  const donationCampaignDetails = useDonationCampaignDetails()

  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const isGbCountry = useIsCountry(Country.GB)
  const summaryTotal = started?.promotion?.owedAfterDiscount ?? started?.consumerAmount ?? checkout?.amount
  const shouldRenderUkRegulatedSummary = isGbCountry && !isConsumerLendingOrder && valueIsNotNullOrUndefined(summaryTotal)
  const shouldHideUkRegulatedTap = shouldRenderUkRegulatedSummary && isExpressCheckout

  const isManualPromoEnabled = useCachedFeatureFlag(Features.CHECKOUT_MANUAL_PROMO_CODE_ENTRY, false)
  const checkoutToken = useCheckoutToken()
  const isManualPromoEligible = isArcadeDesignLanguage && isManualPromoEnabled
  // Mirrors PromoCodeInput's own isAutoApplied logic so we can suppress the Discount
  // row when PromoCodeInput's applied state already shows the discount inline.
  // Auto-applied promos (no matching marker) fall through and render via renderDiscountItem.
  const isPromoUserApplied = isPromoCodeUserApplied(started?.promotion, persistedManualPromo, checkoutToken)
  // A promotion the user didn't enter came from the backend; PromoCodeInput self-hides for it.
  const shouldHidePromoCodeInput = Boolean(started?.promotion) && !isPromoUserApplied

  const purchaseAmount = checkout?.amount
  const selectedShippingOption = isCrossBorder
    ? xbtAllShippingOptions.find((option) => option.id === nonXbtSelectedShippingOption.id)
    : nonXbtSelectedShippingOption
  const shippingAmount = selectedShippingOption?.shippingAmount
  const taxAmount = selectedShippingOption?.taxAmount
  const orderBreakdownType = useOrderBreakdownType(shippingAmount, taxAmount)

  const isEstimatedPpaOrder = useIsEstimatedPPA()
  const isDeferredShipping = useIsDeferredShipping()
  const isExpress = useIsExpress()

  const isExpressOrderUnreconciled = isExpress && orderBreakdownType !== 'Full'
  const shouldHideExpressBreakdownRows = isExpressOrderUnreconciled && !isShippingOptionUpdating
  // The manual-promo flow always renders a breakdown above TotalAmountPayable, so flip
  // orderBreakdownIsVisible to true even when useOrderBreakdownType would otherwise
  // return 'None' (e.g., standard checkout with no promo applied yet).
  const orderBreakdownIsVisible = useIsOrderBreakdownVisible(orderBreakdownType) || isManualPromoEligible
  const { initial } = useCheckoutState()
  const isSupOrder = useIsSupOrder()
  const isExpressWithoutBuyNow = isExpress && !initial.buyNow
  const deferringShippingAddressMode = isDeferredShipping && !nonShippingInfoAddressModes.includes(addressMode)
  const isEstimatedDeferredShippingOrder = deferringShippingAddressMode || isExpressWithoutBuyNow
  const isEstimatedTotal = isEstimatedPpaOrder || isEstimatedDeferredShippingOrder || isSupOrder

  const renderExpressCheckoutItem = (): ReactNode => {
    if (!isExpressCheckout || isNullOrUndefined(selectedShippingOption)) {
      return null
    }

    switch (addressMode) {
      case AddressModeType.ADDRESS_WITH_SHIPPING_OPTIONS:
        return (
          <>
            <OrderBreakdownItem name={t('summary:shipping')} amount={shippingAmount} loading={isShippingOptionUpdating} preferFreeText />
            <OrderBreakdownItem name={t('summary:taxes')} amount={taxAmount} loading={isShippingOptionUpdating} />
          </>
        )
      case AddressModeType.SHIP_TO_ORDER_ADDRESS:
        return (
          <>
            <OrderBreakdownItem name={t('summary:shipping')} amount={shippingAmount} loading={isShippingOptionUpdating} preferFreeText />
            <OrderBreakdownItem name={t('summary:taxes')} amount={taxAmount} loading={isShippingOptionUpdating} />
          </>
        )
      case AddressModeType.PICKUP_FROM_ORDER_ADDRESS:
        return (
          <>
            <OrderBreakdownItem name={t('summary:handling')} amount={shippingAmount} loading={isShippingOptionUpdating} preferFreeText />
            <OrderBreakdownItem name={t('summary:taxes')} amount={taxAmount} loading={isShippingOptionUpdating} />
          </>
        )
    }
  }

  const toogleDisplayDiscountTooltip = (): void => {
    setShowDiscountTooltip(!showDiscountTooltip)
  }

  const renderDiscountTooltip = (): ReactNode => {
    if (!showDiscountTooltip) {
      return null
    }

    const tooltipContent = (): ReactNode => (
      <Text size={smallTextSize}>
        {t('summary:discountTooltip', {
          amount: formatMoney(started?.promotion?.discount.amount),
        })}
      </Text>
    )

    return (
      <Tooltip
        id={DISCOUNT_TOOLTIP_ID}
        triggerRef={discountTooltipRef}
        caretPlacement={CARET_PLACEMENTS.TOP_START}
        usePortal={false}
        content={tooltipContent()}
        skidding={65}
        hasCaret={true}
        useMobileView={false}
        bubbleSize={{ width: '100%' }}
        closeOnOutsideClick={true}
        onClose={toogleDisplayDiscountTooltip}
      />
    )
  }

  const renderDiscountItem = (): ReactNode => {
    const discount = started?.promotion?.discount
    if (isNullOrUndefined(discount)) {
      return null
    }
    // Skip the standalone Discount row when PromoCodeInput is rendered above and is
    // showing the same user-applied discount inline. Auto-applied promos (where
    // PromoCodeInput self-hides) still render the row here so the user sees the line.
    if (isManualPromoEligible && isPromoUserApplied) {
      return null
    }

    const discountTextSize = isArcadeDesignLanguage ? textSize : smallTextSize
    const textColor = isArcadeDesignLanguage ? 'Standard' : 'Subtle'
    const moneyColor = isArcadeDesignLanguage ? 'Success' : 'Moss Mid'
    return (
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text size={discountTextSize} color={textColor}>
            {t('summary:discount')}
            <button
              type='button'
              ref={discountTooltipRef}
              className={styles.discountTooltipIcon}
              onClick={toogleDisplayDiscountTooltip}
              data-testid='discount-tooltip-icon'
              aria-label={t('summary:discountInfo')}
              aria-describedby={showDiscountTooltip ? DISCOUNT_TOOLTIP_ID : undefined}
              aria-expanded={showDiscountTooltip}
            >
              <Icon.Info size='Small' color={textColor} />
            </button>
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Text color={moneyColor} size={discountTextSize} renderAs='span'>
            -
          </Text>
          <Money color={moneyColor} locale={locale} size={discountTextSize} value={discount} testNameSpace='order-breakdown-discount' />
        </GridFixedItem>
      </Grid>
    )
  }

  const renderDonationItem = (): ReactNode => {
    if (donationAmount <= 0 || isNullOrUndefined(donationCampaignDetails)) {
      return null
    }

    const donationMoney = money(donationAmount.toString(), donationCampaignDetails.amount.currency)
    const donationTextSize = isArcadeDesignLanguage ? textSize : smallTextSize
    const textColor = isArcadeDesignLanguage ? 'Standard' : 'Subtle'

    return (
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text size={donationTextSize} color={textColor}>
            {t('orders:orderSummary.donation')}
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Money loading={isUpdatingDonation} color={textColor} locale={locale} size={donationTextSize} value={donationMoney} />
        </GridFixedItem>
      </Grid>
    )
  }

  const renderDueTodayItem = (): ReactNode => {
    const isNpufPaymentType = firstPaymentType === FirstPaymentType.NoPaymentUpfront
    const moneyValue = {
      ...paymentSchedules[0].amount,
      amount: isNpufPaymentType ? '0.00' : paymentSchedules[0].amount.amount,
    }

    return (
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' size={textSize} bold>
            <Trans i18nKey='common:dueToday' />
          </Text>{' '}
          {!isNpufPaymentType && (
            <div className={styles.wrappedOneOfX}>
              <Text renderAs='span' size={textSize} bold>
                {t('orders:orderSummary.oneOfX', { total: paymentSchedules.length })}
              </Text>
            </div>
          )}
        </GridItem>
        <GridFixedItem align='right'>
          <Money bold showAsterisk={isEstimatedTotal} loading={isShippingOptionUpdating} locale={locale} size={textSize} value={moneyValue} />
        </GridFixedItem>
      </Grid>
    )
  }

  const renderDisclaimer = (): ReactNode => {
    if (!isEstimatedTotal) {
      return null
    }

    return (
      <Grid verticalGutter={GUTTER_VARIANT.ONE}>
        <GridItem>
          <Text size={extraSmallTextSize} color='Subtle'>
            {t('orders:orderSummary.estimatedTotalDisclaimer')}
          </Text>
        </GridItem>
      </Grid>
    )
  }

  const renderDivider = (): ReactNode => (
    <Grid>
      <GridItem>
        <Divider kind='Hairline' margin='Half' />
      </GridItem>
    </Grid>
  )

  const renderPromoCodeInput = (): ReactNode => {
    if (shouldHidePromoCodeInput) {
      return null
    }
    return (
      <div className={styles.promoCodeInputWrapper}>
        <PromoCodeInput />
      </div>
    )
  }

  const renderFullOrderBreakdown = (): ReactNode => (
    <>
      {!shouldHideExpressBreakdownRows && (
        <>
          <OrderBreakdownItem loading={isShippingOptionUpdating} name={t('orders:orderSummary.orderAmount')} amount={purchaseAmount} />
          {renderDiscountItem()}
          {renderExpressCheckoutItem()}
          {renderDonationItem()}
        </>
      )}
      {isManualPromoEligible && renderPromoCodeInput()}
      {renderDivider()}
    </>
  )

  const renderDiscountAndDonationOrderBreakdown = (): ReactNode => (
    <>
      {renderDiscountItem()}
      {renderDonationItem()}
      {renderDivider()}
    </>
  )

  const renderUkRegulatedSummary = (): ReactNode => {
    if (!summaryTotal) {
      return null
    }

    return (
      <div className={styles.ukRegulatedSummary}>
        {isManualPromoEligible && renderPromoCodeInput()}
        {renderDiscountItem()}
        <div>
          <OrderBreakdownItem name={t('summary:purchaseCreditAmount')} amount={summaryTotal} loading={isShippingOptionUpdating} />
        </div>
        <div>
          <OrderBreakdownItem name={t('summary:interestZeroPercent')} amount={money('0.00', summaryTotal.currency)} loading={isShippingOptionUpdating} />
        </div>
        <Divider kind='Hairline' margin='No' />
        {!shouldHideUkRegulatedTap && (
          <div className={styles.ukRegulatedTotalAmountPayable}>
            <TotalAmountPayable isEstimatedTotal={isEstimatedTotal} orderBreakdownIsHidden={!orderBreakdownIsVisible || isManualPromoEligible} />
          </div>
        )}
        <div className={styles.ukRegulatedDueToday}>{renderDueTodayItem()}</div>
      </div>
    )
  }

  const renderOrderBreakdown = (): ReactNode => {
    // Manual-promo flow forces the Full layout regardless of orderBreakdownType so
    // PromoCodeInput has a place to live even when no discount/donation/shipping is
    // present yet. When isManualPromoEligible is false, the existing switch covers
    // non-Arcade flows (Bronte / Apple Retail / Pay Monthly) and Arcade flows without
    // the manual-promo flag.
    if (isManualPromoEligible || orderBreakdownType === 'Full') {
      return renderFullOrderBreakdown()
    }
    if (orderBreakdownType === 'DiscountAndDonation') {
      return renderDiscountAndDonationOrderBreakdown()
    }
    return null
  }

  return (
    <div className={styles.container}>
      {shouldRenderUkRegulatedSummary ? (
        renderUkRegulatedSummary()
      ) : (
        <>
          {renderOrderBreakdown()}
          {/* TotalAmountPayable self-hides when the order amount is unchanged AND the
              breakdown is visible (because Purchase amount alone tells the user the total).
              For the Arcade manual-promo flow, Figma requires TAP to render unconditionally
              between PromoCodeInput and Due today even with no promo applied — force the
              `orderBreakdownIsHidden` flag in that branch to bypass the self-hide. */}
          <TotalAmountPayable isEstimatedTotal={isEstimatedTotal} orderBreakdownIsHidden={!orderBreakdownIsVisible || isManualPromoEligible} />
          {renderDueTodayItem()}
        </>
      )}
      {renderDisclaimer()}
      {renderDiscountTooltip()}
    </div>
  )
}
